import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
  TableCell,
  TableRow,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import StarIcon from '@mui/icons-material/Star';
import VideocamIcon from '@mui/icons-material/Videocam';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import * as XLSX from 'xlsx';

import api from '../config/api';
import DataTable from '../components/DataTable';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending Dispatch',
    color: 'warning',
    icon: <HourglassEmptyIcon fontSize="small" />,
    bgColor: '#FFF3E0',
    textColor: '#E65100',
  },
  dispatched: {
    label: 'Dispatched',
    color: 'info',
    icon: <LocalShippingIcon fontSize="small" />,
    bgColor: '#E1F5FE',
    textColor: '#0277BD',
  },
  delivered: {
    label: 'Delivered',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
    bgColor: '#E8F5E9',
    textColor: '#2E7D32',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'error',
    icon: <CancelIcon fontSize="small" />,
    bgColor: '#FFEBEE',
    textColor: '#C62828',
  },
};

const COURIER_OPTIONS = [
  'DTDC',
  'Blue Dart',
  'Delhivery',
  'Speed Post',
  'India Post',
  'Shadowfax',
  'Xpressbees',
  'Ecom Express',
  'Professional Couriers',
];

function Rewards() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Level 1 Rewards, 1 = Level 2 Milestones
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_count: 0,
    pending_count: 0,
    dispatched_count: 0,
    delivered_count: 0,
    cancelled_count: 0,
  });

  // Level 1 Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Level 2 Dedicated Tab State
  const [milestoneTrainers, setMilestoneTrainers] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestoneSearch, setMilestoneSearch] = useState('');

  // Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [formData, setFormData] = useState({
    status: 'pending',
    courier_name: '',
    tracking_number: '',
    delivery_address: '',
    notes: '',
    play_store_reviews_count: 0,
    testimonial_videos_count: 0,
    qr_scans_count: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState(null);

  // Notification Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      const res = await api.get(`/admin/rewards?${params.toString()}`);
      if (res.data?.success) {
        setRewards(res.data.data || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      }
    } catch (error) {
      console.error('Error fetching rewards:', error);
      showToast(error.response?.data?.error || 'Failed to fetch reward requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      setMilestonesLoading(true);
      const params = new URLSearchParams();
      if (milestoneSearch.trim()) params.append('search', milestoneSearch.trim());

      const res = await api.get(`/admin/rewards/trainers/milestones?${params.toString()}`);
      if (res.data?.success) {
        setMilestoneTrainers(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching milestone trainers:', error);
      showToast(error.response?.data?.error || 'Failed to fetch trainer milestones', 'error');
    } finally {
      setMilestonesLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchMilestones();
    }
  }, [activeTab]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 0) {
      fetchRewards();
    } else {
      fetchMilestones();
    }
  };

  // Stepper update handler for Play Store Reviews, Testimonial Videos, and QR Code Scans
  const handleUpdateMilestone = async (trainerId, type, delta, trainerName) => {
    try {
      const payload = {};
      if (type === 'reviews') payload.delta_reviews = delta;
      if (type === 'videos') payload.delta_videos = delta;
      if (type === 'qr') payload.delta_qr = delta;

      // Optimistic update on rewards list
      setRewards((prev) =>
        prev.map((r) => {
          if (r.trainer_id === trainerId) {
            const curRev = Number(r.play_store_reviews_count || 0);
            const curVid = Number(r.testimonial_videos_count || 0);
            const curQr = Number(r.qr_scans_count || 0);
            return {
              ...r,
              play_store_reviews_count: type === 'reviews' ? Math.max(0, curRev + delta) : curRev,
              testimonial_videos_count: type === 'videos' ? Math.max(0, curVid + delta) : curVid,
              qr_scans_count: type === 'qr' ? Math.max(0, Math.min(5, curQr + delta)) : curQr,
            };
          }
          return r;
        })
      );

      // Optimistic update on dedicated milestoneTrainers list
      setMilestoneTrainers((prev) =>
        prev.map((t) => {
          if (t.trainer_id === trainerId) {
            const curRev = Number(t.play_store_reviews_count || 0);
            const curVid = Number(t.testimonial_videos_count || 0);
            const curQr = Number(t.qr_scans_count || 0);
            const newRev = type === 'reviews' ? Math.max(0, curRev + delta) : curRev;
            const newVid = type === 'videos' ? Math.max(0, curVid + delta) : curVid;
            const newQr = type === 'qr' ? Math.max(0, Math.min(5, curQr + delta)) : curQr;
            return {
              ...t,
              play_store_reviews_count: newRev,
              testimonial_videos_count: newVid,
              qr_scans_count: newQr,
              level2_status: newRev >= 5 && newVid >= 5 && newQr >= 5 ? 'completed' : 'in_progress',
            };
          }
          return t;
        })
      );

      const res = await api.patch(`/admin/rewards/trainer/${trainerId}/milestones`, payload);
      if (res.data?.success) {
        const d = res.data.data;
        let label = '';
        if (type === 'reviews') label = `${d.play_store_reviews_count}/5 Play Store Reviews`;
        else if (type === 'videos') label = `${d.testimonial_videos_count}/5 Testimonial Videos`;
        else if (type === 'qr') label = `${d.qr_scans_count}/5 QR Code Scans`;
        showToast(`Updated ${label} for ${trainerName || 'Coach'}`);
      }
    } catch (error) {
      console.error('Error updating milestone:', error);
      showToast(error.response?.data?.error || 'Failed to update milestone', 'error');
      fetchRewards();
      fetchMilestones();
    }
  };

  const handleOpenEdit = (reward) => {
    setSelectedReward(reward);
    setFormData({
      status: reward.status || 'pending',
      courier_name: reward.courier_name || '',
      tracking_number: reward.tracking_number || '',
      delivery_address: reward.delivery_address || '',
      notes: reward.notes || '',
      play_store_reviews_count: reward.play_store_reviews_count || 0,
      testimonial_videos_count: reward.testimonial_videos_count || 0,
      qr_scans_count: reward.qr_scans_count || 0,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedReward) return;
    try {
      setSubmitting(true);
      const res = await api.put(`/admin/rewards/${selectedReward.id}`, formData);
      if (res.data?.success) {
        // Also update milestones if changed in dialog
        await api.patch(`/admin/rewards/trainer/${selectedReward.trainer_id}/milestones`, {
          play_store_reviews_count: formData.play_store_reviews_count,
          testimonial_videos_count: formData.testimonial_videos_count,
          qr_scans_count: formData.qr_scans_count,
        });

        showToast('Reward request & milestones updated successfully!');
        setEditDialogOpen(false);
        fetchRewards();
        if (activeTab === 1) fetchMilestones();
      }
    } catch (error) {
      console.error('Error updating reward:', error);
      showToast(error.response?.data?.error || 'Failed to update reward request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDelete = (reward) => {
    setRewardToDelete(reward);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rewardToDelete) return;
    try {
      setSubmitting(true);
      await api.delete(`/admin/rewards/${rewardToDelete.id}`);
      showToast('Reward request deleted');
      setDeleteDialogOpen(false);
      fetchRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
      showToast(error.response?.data?.error || 'Failed to delete reward request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`, 'info');
  };

  // Export to Excel for Logistics Partners
  const handleExportExcel = () => {
    if (rewards.length === 0) {
      showToast('No reward requests to export', 'warning');
      return;
    }

    const exportData = rewards.map((r, index) => ({
      'S.No': index + 1,
      'Request ID': r.id,
      'Date': r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '',
      'Trainer ID': r.trainer_id,
      'Trainer Name': r.trainer_name,
      'Phone': r.trainer_phone,
      'Email': r.trainer_email,
      'District': r.trainer_district || '',
      'Referral Code': r.trainer_reference_code || '',
      'Level': `Level ${r.level}`,
      'Reward Type': r.reward_type === 'tshirt' ? 'Mr.Coach T-Shirt' : r.reward_type,
      'Play Store Reviews (Max 5)': r.play_store_reviews_count || 0,
      'Testimonial Videos (Max 5)': r.testimonial_videos_count || 0,
      'Delivery Address': r.delivery_address,
      'Status': r.status?.toUpperCase(),
      'Courier Partner': r.courier_name || '',
      'Tracking Number': r.tracking_number || '',
      'Dispatched At': r.dispatched_at ? new Date(r.dispatched_at).toLocaleString('en-IN') : '',
      'Delivered At': r.delivered_at ? new Date(r.delivered_at).toLocaleString('en-IN') : '',
      'Notes': r.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'T-Shirt Requests');

    // Column widths
    ws['!cols'] = [
      { wch: 6 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 22 },
      { wch: 14 },
      { wch: 24 },
      { wch: 14 },
      { wch: 18 },
      { wch: 10 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 45 },
      { wch: 14 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
    ];

    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `MrCoach_Tshirt_Rewards_${dateStr}.xlsx`);
    showToast('Exported to Excel successfully!');
  };

  // Reusable Stepper Widget for Play Store Reviews and Testimonial Videos
  const renderMilestoneStepper = (trainerId, count, type, trainerName) => {
    const current = Number(count || 0);
    const isComplete = current >= 5;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: isComplete ? '#E8F5E9' : '#FFF8E1',
          borderRadius: 2,
          p: '2px 6px',
          border: `1px solid ${isComplete ? '#A5D6A7' : '#FFE082'}`,
        }}
      >
        <Tooltip title="Decrement count (-1)">
          <span>
            <IconButton
              size="small"
              disabled={current <= 0}
              onClick={() => handleUpdateMilestone(trainerId, type, -1, trainerName)}
              sx={{
                p: 0.2,
                bgcolor: '#FFFFFF',
                '&:hover': { bgcolor: '#EEEEEE' },
                width: 22,
                height: 22,
              }}
            >
              <RemoveIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 12,
            minWidth: 34,
            textAlign: 'center',
            color: isComplete ? '#2E7D32' : '#B78103',
          }}
        >
          {current} / 5
        </Typography>
        <Tooltip title="Increment count (+1)">
          <IconButton
            size="small"
            onClick={() => handleUpdateMilestone(trainerId, type, 1, trainerName)}
            sx={{
              p: 0.2,
              bgcolor: '#FFFFFF',
              '&:hover': { bgcolor: '#EEEEEE' },
              width: 22,
              height: 22,
            }}
          >
            <AddIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columns = [
    'ID / Date',
    'Trainer Details',
    'Reward & Level',
    'Level 2 Milestones',
    'Delivery Address',
    'Status',
    'Courier & Tracking',
    'Notes',
    'Actions',
  ];

  const milestoneColumns = [
    'Trainer ID',
    'Coach Details',
    'District / Code',
    'Play Store Reviews (Target: 5)',
    'Testimonial Videos (Target: 5)',
    'QR Code Scans (Target: 5)',
    'Level 2 Status',
    'WhatsApp Contact',
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CardGiftcardIcon sx={{ fontSize: 36, color: '#03a9f4' }} />
            Trainer Rewards & Milestones Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track Level 1 T-Shirt dispatches and verify Level 2 client reviews & testimonial videos sent via WhatsApp.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={activeTab === 0 ? fetchRewards : fetchMilestones}
            disabled={loading || milestonesLoading}
          >
            Refresh
          </Button>
          {activeTab === 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={rewards.length === 0}
              sx={{ fontWeight: 700 }}
            >
              Export Shipping Sheet
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<CardGiftcardIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Level 1 T-Shirt Rewards (${summary.total_count})`}
            sx={{ fontWeight: 700, textTransform: 'none', py: 1.5 }}
          />
          <Tab
            icon={<StarIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Level 2 Milestones (Reviews & Videos)"
            sx={{ fontWeight: 700, textTransform: 'none', py: 1.5 }}
          />
        </Tabs>
      </Paper>

      {/* ==================================================================== */}
      {/* TAB 0: LEVEL 1 T-SHIRT REWARDS & DISPATCHES                          */}
      {/* ==================================================================== */}
      {activeTab === 0 && (
        <>
          {/* Metrics Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#EDE7F6', borderRadius: 2, border: '1px solid #D1C4E9' }}>
                <Typography variant="body2" sx={{ color: '#512DA8', fontWeight: 600 }}>Total Requests</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#311B92', my: 0.5 }}>
                  {summary.total_count}
                </Typography>
                <Typography variant="caption" color="text.secondary">All Level 1 completions</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#FFF8E1', borderRadius: 2, border: '1px solid #FFE082' }}>
                <Typography variant="body2" sx={{ color: '#F57F17', fontWeight: 600 }}>Pending Dispatch</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#E65100', my: 0.5 }}>
                  {summary.pending_count}
                </Typography>
                <Typography variant="caption" color="text.secondary">Requires packing & courier dispatch</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#E1F5FE', borderRadius: 2, border: '1px solid #81D4FA' }}>
                <Typography variant="body2" sx={{ color: '#0277BD', fontWeight: 600 }}>Dispatched / In Transit</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#01579B', my: 0.5 }}>
                  {summary.dispatched_count}
                </Typography>
                <Typography variant="caption" color="text.secondary">Shipped with tracking details</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#E8F5E9', borderRadius: 2, border: '1px solid #A5D6A7' }}>
                <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600 }}>Delivered</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B5E20', my: 0.5 }}>
                  {summary.delivered_count}
                </Typography>
                <Typography variant="caption" color="text.secondary">Completed orders</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Search & Filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <form onSubmit={handleSearchSubmit}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search coach name, phone, code, courier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending Dispatch</MenuItem>
                    <MenuItem value="dispatched">Dispatched / In Transit</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button variant="contained" onClick={fetchRewards} sx={{ fontWeight: 700 }}>
                  Apply Filters
                </Button>
                {(statusFilter !== 'all' || searchTerm) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          <DataTable
            columns={columns}
            data={rewards}
            loading={loading}
            emptyMessage="No reward requests found matching your filters."
            renderRow={(item) => {
              const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
              return (
                <TableRow key={item.id} hover>
                  {/* ID / Date */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      #{item.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '-'}
                    </Typography>
                  </TableCell>

                  {/* Trainer Details */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121' }}>
                      {item.trainer_name || 'Coach'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                      <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item.trainer_phone || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                      <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item.trainer_email || '-'}
                      </Typography>
                    </Box>
                    {item.trainer_reference_code && (
                      <Chip
                        label={item.trainer_reference_code}
                        size="small"
                        sx={{ mt: 0.5, fontSize: 10, height: 18, bgcolor: '#FFF8E1', color: '#B78103' }}
                      />
                    )}
                  </TableCell>

                  {/* Reward & Level */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip
                      label="Level 1"
                      size="small"
                      sx={{ bgcolor: '#FFF9C4', color: '#F57F17', fontWeight: 700, mb: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      👕 Mr.Coach T-Shirt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Certified Coach Kit
                    </Typography>
                  </TableCell>

                  {/* Level 2 Milestones (Reviews & Videos Steppers) */}
                  <TableCell sx={{ minWidth: 240 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Play Store Reviews */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 15, color: '#F57F17' }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#37474F' }}>
                            Play Store:
                          </Typography>
                        </Box>
                        {renderMilestoneStepper(item.trainer_id, item.play_store_reviews_count, 'reviews', item.trainer_name)}
                      </Box>

                      {/* Testimonial Videos */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VideocamIcon sx={{ fontSize: 15, color: '#2E7D32' }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#37474F' }}>
                            Videos:
                          </Typography>
                        </Box>
                        {renderMilestoneStepper(item.trainer_id, item.testimonial_videos_count, 'videos', item.trainer_name)}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Delivery Address */}
                  <TableCell sx={{ minWidth: 260, maxWidth: 340 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.2,
                        bgcolor: '#FAFAFA',
                        borderRadius: 1.5,
                        border: '1px solid #E0E0E0',
                        position: 'relative',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {item.delivery_address || 'No address provided'}
                      </Typography>
                      {item.delivery_address && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <Tooltip title="Copy Address">
                            <Button
                              size="small"
                              startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                              onClick={() => handleCopy(item.delivery_address, 'Address')}
                              sx={{ fontSize: 11, py: 0, px: 0.8, minWidth: 0, textTransform: 'none' }}
                            >
                              Copy
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                    </Paper>
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      sx={{
                        bgcolor: statusConfig.bgColor,
                        color: statusConfig.textColor,
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>

                  {/* Courier & Tracking */}
                  <TableCell sx={{ minWidth: 180 }}>
                    {item.tracking_number ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0277BD' }}>
                          {item.courier_name || 'Courier'}:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {item.tracking_number}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(item.tracking_number, 'Tracking Number')}
                          >
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                        {item.dispatched_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Dispatched: {new Date(item.dispatched_at).toLocaleDateString('en-IN')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#9E9E9E', fontStyle: 'italic' }}>
                        Not dispatched yet
                      </Typography>
                    )}
                  </TableCell>

                  {/* Notes */}
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {item.notes || '-'}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Update Status / Dispatch / Milestones">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenEdit(item)}
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Record">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDelete(item)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            }}
          />
        </>
      )}

      {/* ==================================================================== */}
      {/* TAB 1: LEVEL 2 TRAINER MILESTONES (ALL TRAINERS)                     */}
      {/* ==================================================================== */}
      {activeTab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8} md={6}>
                <form onSubmit={handleSearchSubmit}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search coach by name, phone, email, or reference code..."
                    value={milestoneSearch}
                    onChange={(e) => setMilestoneSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Grid>
              <Grid item xs={12} sm={4} md={6} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button variant="contained" onClick={fetchMilestones} sx={{ fontWeight: 700 }}>
                  Search Coaches
                </Button>
                {milestoneSearch && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setMilestoneSearch('');
                      fetchMilestones();
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>

          <DataTable
            columns={milestoneColumns}
            data={milestoneTrainers}
            loading={milestonesLoading}
            emptyMessage="No trainers found."
            renderRow={(trainer) => {
              const revCount = Number(trainer.play_store_reviews_count || 0);
              const vidCount = Number(trainer.testimonial_videos_count || 0);
              const qrCount = Number(trainer.qr_scans_count || 0);
              const isLevel2Complete = revCount >= 5 && vidCount >= 5 && qrCount >= 5;

              return (
                <TableRow key={trainer.trainer_id} hover>
                  {/* Trainer ID */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#311B92' }}>
                      #{trainer.trainer_id}
                    </Typography>
                  </TableCell>

                  {/* Coach Details */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121' }}>
                      {trainer.trainer_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                      <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {trainer.trainer_phone || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                      <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {trainer.trainer_email || '-'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* District / Code */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {trainer.trainer_district || 'District N/A'}
                    </Typography>
                    {trainer.trainer_reference_code && (
                      <Chip
                        label={trainer.trainer_reference_code}
                        size="small"
                        sx={{ mt: 0.5, fontSize: 10, height: 18, bgcolor: '#FFF8E1', color: '#B78103' }}
                      />
                    )}
                  </TableCell>

                  {/* Play Store Reviews Stepper */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon sx={{ fontSize: 20, color: revCount >= 5 ? '#2E7D32' : '#F57F17' }} />
                      {renderMilestoneStepper(trainer.trainer_id, revCount, 'reviews', trainer.trainer_name)}
                    </Box>
                  </TableCell>

                  {/* Testimonial Videos Stepper */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VideocamIcon sx={{ fontSize: 20, color: vidCount >= 5 ? '#2E7D32' : '#0277BD' }} />
                      {renderMilestoneStepper(trainer.trainer_id, vidCount, 'videos', trainer.trainer_name)}
                    </Box>
                  </TableCell>

                  {/* QR Code Scans Stepper */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QrCodeScannerIcon sx={{ fontSize: 20, color: qrCount >= 5 ? '#2E7D32' : '#6A1B9A' }} />
                      {renderMilestoneStepper(trainer.trainer_id, qrCount, 'qr', trainer.trainer_name)}
                    </Box>
                  </TableCell>

                  {/* Level 2 Status */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      <Chip
                        icon={isLevel2Complete ? <CheckCircleIcon fontSize="small" /> : <HourglassEmptyIcon fontSize="small" />}
                        label={isLevel2Complete ? 'Level 2 Completed 🎉' : 'In Progress'}
                        sx={{
                          bgcolor: isLevel2Complete ? '#E8F5E9' : '#FFF3E0',
                          color: isLevel2Complete ? '#2E7D32' : '#E65100',
                          fontWeight: 700,
                        }}
                      />
                      {isLevel2Complete && (
                        <Chip
                          size="small"
                          icon={<EmailIcon sx={{ fontSize: '13px !important' }} />}
                          label={Boolean(trainer.level2_email_sent) ? 'Bank Details Email Sent' : 'Bank Details Email Pending'}
                          sx={{
                            bgcolor: Boolean(trainer.level2_email_sent) ? '#EDE7F6' : '#FFFDE7',
                            color: Boolean(trainer.level2_email_sent) ? '#512DA8' : '#F57F17',
                            fontSize: '0.72rem',
                            height: 22,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  {/* WhatsApp Action */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {trainer.trainer_phone ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                        href={`https://wa.me/${trainer.trainer_phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}
                      >
                        WhatsApp
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No phone</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            }}
          />
        </>
      )}

      {/* Edit / Dispatch Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !submitting && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, bgcolor: '#F5F5F5', pb: 1.5 }}>
          📦 Update Dispatch & Level Milestones
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {selectedReward && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#EDE7F6', borderRadius: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#311B92' }}>
                Coach: {selectedReward.trainer_name} (#{selectedReward.trainer_id})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Phone: {selectedReward.trainer_phone} | Email: {selectedReward.trainer_email}
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Status Selector */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Order / Reward Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Order / Reward Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="pending">⏳ Pending Dispatch</MenuItem>
                  <MenuItem value="dispatched">🚚 Dispatched (Notify Coach with Tracking)</MenuItem>
                  <MenuItem value="delivered">✅ Delivered (Notify Coach)</MenuItem>
                  <MenuItem value="cancelled">❌ Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Courier Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Courier Partner"
                value={formData.courier_name}
                onChange={(e) => setFormData({ ...formData, courier_name: e.target.value })}
                placeholder="e.g. DTDC, Blue Dart"
              />
            </Grid>

            {/* Tracking Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Tracking Number"
                value={formData.tracking_number}
                onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                placeholder="e.g. D12345678"
              />
            </Grid>

            {/* Level 2 Milestones Adjustment */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#FAFAFA', borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#455A64', display: 'block', mb: 1 }}>
                  ⭐ Level 2 Milestones Verified by Admin:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Play Store Reviews (Max 5)"
                      inputProps={{ min: 0, max: 5 }}
                      value={formData.play_store_reviews_count}
                      onChange={(e) => setFormData({ ...formData, play_store_reviews_count: parseInt(e.target.value, 10) || 0 })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Testimonial Videos (Max 5)"
                      inputProps={{ min: 0, max: 5 }}
                      value={formData.testimonial_videos_count}
                      onChange={(e) => setFormData({ ...formData, testimonial_videos_count: parseInt(e.target.value, 10) || 0 })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="QR Code Scans (Max 5)"
                      inputProps={{ min: 0, max: 5 }}
                      value={formData.qr_scans_count || 0}
                      onChange={(e) => setFormData({ ...formData, qr_scans_count: parseInt(e.target.value, 10) || 0 })}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Delivery Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Delivery Address"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder="Full delivery address provided by coach"
              />
            </Grid>

            {/* Admin Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Internal Admin Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g. Size L dispatched via Chennai hub"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#F5F5F5' }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{ fontWeight: 700 }}
          >
            Save & Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Delete Reward Request?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this reward request for{' '}
            <strong>{rewardToDelete?.trainer_name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={submitting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Rewards;